import { toDateFromServer } from './dates';
import type { Language } from '../../../src/api/langConfig';

export type ClosureText = { el?: string; en?: string };
export type ClosureInfo = { closedText: string; closedIcon: string };

export type SchoolClosedRangeRecord = {
    start?: string;
    end?: string;
    closedText?: ClosureText;
    closedIcon?: string;
};

export type ClassroomClosedRangeRecord = SchoolClosedRangeRecord & {
    classrooms?: Record<string, boolean | unknown>;
};

function closureTextForLang(closedText: ClosureText | undefined, lang: Language): string | undefined {
    if (!closedText) return undefined;
    return closedText[lang] || closedText.el || closedText.en;
}

function isDateInRange(date: string, startDate: string, endDate: string): boolean {
    if (!date) {
        return false;
    }
    return toDateFromServer(date) >= toDateFromServer(startDate) && toDateFromServer(date) <= toDateFromServer(endDate);
}

function closureFromRange(range: SchoolClosedRangeRecord, lang: Language): ClosureInfo | null {
    const startDate = range.start;
    const endDate = range.end;
    if (!startDate || !endDate) return null;
    const text = closureTextForLang(range.closedText, lang);
    if (!text) return null;
    return {
        closedText: text,
        closedIcon: range.closedIcon || 'closed.svg',
    };
}

/** Last matching whole-school range for the date. */
export function resolveWholeSchoolClosure(
    date: string,
    ranges: SchoolClosedRangeRecord[] | null | undefined,
    lang: Language,
): ClosureInfo | null {
    let result: ClosureInfo | null = null;
    ranges?.forEach((range) => {
        const startDate = range.start;
        const endDate = range.end;
        if (startDate && endDate && isDateInRange(date, startDate, endDate)) {
            const fromRange = closureFromRange(range, lang);
            if (fromRange) {
                result = fromRange;
            }
        }
    });
    return result;
}

export function isWholeSchoolClosedDay(
    date: string,
    ranges: SchoolClosedRangeRecord[] | null | undefined,
): boolean {
    return !!ranges?.some((range) => {
        const startDate = range.start;
        const endDate = range.end;
        return !!(startDate && endDate && isDateInRange(date, startDate, endDate)
            && (range.closedText?.el || range.closedText?.en));
    });
}

/** Last matching classroom range for the given classroom key. Skips when whole-school closed. */
export function resolveClassroomClosure(
    date: string,
    classroomKey: string | null | undefined,
    ranges: ClassroomClosedRangeRecord[] | null | undefined,
    lang: Language,
    wholeSchoolClosed: boolean,
): ClosureInfo | null {
    if (wholeSchoolClosed || !classroomKey || classroomKey === 'unknown') return null;

    let result: ClosureInfo | null = null;
    ranges?.forEach((range) => {
        const startDate = range.start;
        const endDate = range.end;
        if (!startDate || !endDate || !isDateInRange(date, startDate, endDate)) return;
        if (!range.classrooms?.[classroomKey]) return;
        const fromRange = closureFromRange(range, lang);
        if (fromRange) {
            result = fromRange;
        }
    });
    return result;
}

/** Union of classroom keys closed on a date (ignores whole-school; caller should check that first). */
export function getClosedClassroomKeysForDate(
    date: string,
    ranges: ClassroomClosedRangeRecord[] | null | undefined,
): Set<string> {
    const keys = new Set<string>();
    ranges?.forEach((range) => {
        const startDate = range.start;
        const endDate = range.end;
        if (!startDate || !endDate || !isDateInRange(date, startDate, endDate)) return;
        Object.keys(range.classrooms ?? {}).forEach((key) => {
            if (range.classrooms?.[key]) {
                keys.add(key);
            }
        });
    });
    return keys;
}

export function hasClassroomClosureForDate(
    date: string,
    ranges: ClassroomClosedRangeRecord[] | null | undefined,
): boolean {
    return getClosedClassroomKeysForDate(date, ranges).size > 0;
}

/** Placed order on a closure day where whole school or a recipient's classroom is closed. */
export function hasClosureAffectingPlacedOrder(
    date: string,
    schoolRanges: SchoolClosedRangeRecord[] | null | undefined,
    classroomRanges: ClassroomClosedRangeRecord[] | null | undefined,
    hasPlacedOrder: boolean,
    recipientsWithPlacedContent: Iterable<string>,
    recipientClassroomKey: (recipientKey: string) => string | null | undefined,
): boolean {
    if (!hasPlacedOrder) {
        return false;
    }
    if (isWholeSchoolClosedDay(date, schoolRanges)) {
        return true;
    }
    const closedClassrooms = getClosedClassroomKeysForDate(date, classroomRanges);
    if (closedClassrooms.size === 0) {
        return false;
    }
    for (const recipientKey of recipientsWithPlacedContent) {
        const classroomKey = recipientClassroomKey(recipientKey);
        if (classroomKey && closedClassrooms.has(classroomKey)) {
            return true;
        }
    }
    return false;
}
